import { type FC, useMemo, useState } from 'react';
import BaseLayout from '@/base/Layout';
import { useParams } from 'react-router-dom';
import SmartWindow from '@/base/Layout/SmartWindow';
import AdminSideNav from '@/account/component/Layout/SideNav';
import {
  UiHStack,
  UiStack,
  uiStyles,
  UiText,
  UiIconPencilSimple,
  UiIconKey
} from '@/lib/ui';
import BreadcrumbBody from '@/base/Layout/BreadcrumbBody';
import type { BaseBreadcrumbBarNode } from '@/base/BreadcrumbBar';
import { generatePageUrl } from '@/app/pages';
import { useQuery } from '@tanstack/react-query';
import {
  loadUserById,
  userIdQueryKey,
  type User
} from '@/api/admin/user';
import { useTenantApi } from '@/account/hook/useTenantApi';
import { useTenantRoute } from '@/account/hook/useTenantRoute';
import UserSummary from './UserSummary';
import QueryContainer from '@/base/QueryContainer/QueryContainer';
import { useApiErrorHandler } from '@/account/hook/useApiErrorHandler';
import BaseButtonIconText from '@/base/Button/IconText';
import { useDisclosure } from '@chakra-ui/react';
import PersonalInfoSection from './PersonalInfoSection';
import SocialLinksSection from './SocialLinksSection';
import UserEditDrawer from './UserEditDrawer';
import PasscodeRevealModal from './PasscodeRevealModal';
import BaseDividerHorizontal from '@/base/Divider/Horizontal';

interface RouteParams {
  userId?: string
  tenantCode?: string
  [key: string]: string | undefined
}

const AccountAccessUserInfoPage: FC = () => {
  const { createTenantAdminApiRequest } = useTenantApi();
  const { tenantCode, tenant } = useTenantRoute();
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const params = useParams<RouteParams>();
  const { isOpen: isEditDrawerOpen, onToggle: onEditDrawerToggle, onClose: onEditDrawerClose } = useDisclosure();
  const { isOpen: isPasscodeModalOpen, onToggle: onPasscodeModalToggle, onClose: onPasscodeModalClose } = useDisclosure();

  const { reportToGlobal } = useApiErrorHandler();
  const userId = useMemo<number | null>(
    () => { return params.userId ? parseInt(params.userId, 10) : null; },
    [params.userId]
  );

  const breadcrumbNodes = useMemo<BaseBreadcrumbBarNode[]>(
    () => {
      return [
        { label: 'Users' },
        { label: 'All Users', url: generatePageUrl('AccountAccessUsers', { tenantCode: tenant?.code ?? '' }) },
        { label: `User information (${userId ?? ''})` }
      ];
    },
    [userId, tenant]
  );

  const userQuery = useQuery<User, Error>({
    queryKey: [userIdQueryKey, { id: userId }],
    queryFn: async (): Promise<User> => {
      const response = await loadUserById(createTenantAdminApiRequest)({ id: userId! });
      return response.item;
    },
    enabled: !!userId
  });

  return (
    <BaseLayout
      smartWindow={(<SmartWindow />)}
      sideMenu={(<AdminSideNav activeNodes={['userAccess', 'tenantUsers']} />)}
      MainTransition={null}
      appSwitch={(<UiHStack flexGrow={1} />)}
    >
      <BreadcrumbBody
        breadcrumbNodes={breadcrumbNodes}
      >
        <QueryContainer query={userQuery} loadingPadding={8}>
          {(user) => {
            return (
              <>
                <UiStack p={8} spacing={8}>
                  <UiStack spacing={4}>
                    <UiHStack justifyContent={'flex-start'} spacing={4} alignItems={'center'}>
                      <UiText variant={'title'}>Summary</UiText>
                      <BaseButtonIconText
                        Icon={UiIconPencilSimple}
                        onClick={onEditDrawerToggle}
                      >
                        Edit user
                      </BaseButtonIconText>
                      <BaseButtonIconText
                        Icon={UiIconKey}
                        onClick={onPasscodeModalToggle}
                      >
                        Reveal last passcode
                      </BaseButtonIconText>
                    </UiHStack>
                    <UserSummary user={user} />
                  </UiStack>
                  
                  <BaseDividerHorizontal height={1} />
                  
                  <UiStack spacing={4}>
                    <UiText variant={'title'}>Personal Information</UiText>
                    <UiStack
                      p={8}
                      py={6}
                      bgColor={'#fff'}
                      shadow={uiStyles.cardShadow}
                      borderRadius={uiStyles.borderRadius}
                    >
                      <PersonalInfoSection user={user} />
                    </UiStack>
                  </UiStack>
                  
                  <BaseDividerHorizontal height={1} />
                  
                  <UiStack spacing={4}>
                    <UiText variant={'title'}>Social Links</UiText>
                    <UiStack
                      p={8}
                      py={6}
                      bgColor={'#fff'}
                      shadow={uiStyles.cardShadow}
                      borderRadius={uiStyles.borderRadius}
                    >
                      <SocialLinksSection user={user} />
                    </UiStack>
                  </UiStack>
                </UiStack>
                
                <UserEditDrawer
                  isOpen={isEditDrawerOpen}
                  onClose={onEditDrawerClose}
                  user={user}
                />
                
                <PasscodeRevealModal
                  isOpen={isPasscodeModalOpen}
                  onClose={onPasscodeModalClose}
                  userId={user.id}
                  userEmail={user.email}
                />
              </>
            );
          }}
        </QueryContainer>
      </BreadcrumbBody>
    </BaseLayout>
  );
};

export default AccountAccessUserInfoPage;